/**
 * Forces muted inline autoplay on Safari (iOS + macOS).
 * HTML `autoplay` fails when videos are display:none, not yet buffered,
 * competing with other autoplay videos, or in Low Power Mode.
 *
 * Collection heroes mount desktop + mobile pairs — only the visible one plays.
 *
 * `play()` called synchronously inside a tap/pointerup is allowed even when
 * autoplay is blocked, so user-initiated slide changes start their videos here.
 */

import { SlideshowSelectEvent } from '@theme/events';

const AUTOPLAY_SELECTOR = 'video[autoplay]';
const RETRY_DELAYS_MS = [0, 150, 400, 1000, 2500];

/**
 * @param {HTMLVideoElement} video
 * @returns {boolean}
 */
function isRendered(video) {
  return video.getClientRects().length > 0;
}

/**
 * @param {HTMLVideoElement} video
 */
function prepare(video) {
  video.muted = true;
  video.defaultMuted = true;
  video.volume = 0;
  video.playsInline = true;
  video.setAttribute('muted', '');
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
}

/**
 * Park hidden (display:none) autoplay videos so Safari does not spend its
 * autoplay budget on them instead of the visible hero.
 * @param {HTMLVideoElement} video
 */
function park(video) {
  prepare(video);
  if (!video.paused) video.pause();
  video.preload = 'none';
}

/**
 * @param {HTMLVideoElement} video
 */
function tryPlay(video) {
  prepare(video);

  if (!isRendered(video)) {
    park(video);
    return;
  }

  if (video.preload === 'none' || video.preload === 'metadata') {
    video.preload = 'auto';
  }

  if (!video.paused && !video.ended) return;

  const playPromise = video.play();
  if (playPromise !== undefined) {
    playPromise.catch(() => {
      /* Safari rejects until buffered or a user gesture; retries handle it. */
    });
  }
}

/**
 * @param {HTMLVideoElement} video
 */
function watch(video) {
  if (video.dataset.iosAutoplayWatched === 'true') {
    tryPlay(video);
    return;
  }

  video.dataset.iosAutoplayWatched = 'true';
  prepare(video);

  const onReady = () => tryPlay(video);
  video.addEventListener('loadedmetadata', onReady);
  video.addEventListener('loadeddata', onReady);
  video.addEventListener('canplay', onReady);
  video.addEventListener('canplaythrough', onReady);

  /* Clicking the Safari play overlay (or the poster) should start playback. */
  video.addEventListener('click', () => tryPlay(video));

  if (isRendered(video)) {
    video.preload = 'auto';
    tryPlay(video);
    RETRY_DELAYS_MS.forEach((delay) => {
      setTimeout(() => tryPlay(video), delay);
    });
  } else {
    park(video);
  }

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && isRendered(video)) {
          tryPlay(video);
        } else if (!isRendered(video)) {
          park(video);
        }
      }
    },
    { threshold: 0.01 }
  );

  io.observe(video);
}

/**
 * @param {Element | Document} root
 */
function scan(root = document) {
  /** @type {HTMLVideoElement[]} */
  const videos = [];

  if (root instanceof HTMLVideoElement && root.hasAttribute('autoplay')) {
    videos.push(root);
  }

  if (root instanceof Element || root instanceof Document) {
    root.querySelectorAll(AUTOPLAY_SELECTOR).forEach((node) => {
      if (node instanceof HTMLVideoElement) videos.push(node);
    });
  }

  videos.forEach(watch);
}

scan();

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node instanceof HTMLElement) scan(node);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

/**
 * Starts the videos of a slide selected by the user, while the gesture is still active.
 * @param {Event} event
 */
function playSelectedSlide(event) {
  if (!(event instanceof SlideshowSelectEvent) || !event.detail.userInitiated) return;

  const { slide } = event.detail;
  if (!(slide instanceof HTMLElement)) return;

  for (const video of slide.querySelectorAll(AUTOPLAY_SELECTOR)) {
    if (!(video instanceof HTMLVideoElement)) continue;

    prepare(video);
    video.preload = 'auto';
    if (video.paused) video.play().catch(() => {});
  }
}

document.addEventListener(SlideshowSelectEvent.eventName, playSelectedSlide);

const retry = () => scan();

document.addEventListener('touchstart', retry, { passive: true });
document.addEventListener('touchend', retry, { passive: true });
document.addEventListener('pointerup', retry, { passive: true });
document.addEventListener('click', retry);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') retry();
});
window.addEventListener('pageshow', retry);
window.addEventListener('resize', retry);
